/**
 * ErrorBoundary - React Error Boundary Component
 * Catches rendering errors in child components and displays fallback UI.
 * Prevents entire app crashes from component-level errors.
 */

import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react';
import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Box p={6} bg="red.50" borderRadius="md" border="1px solid" borderColor="red.200">
          <VStack gap={4} align="stretch">
            <Heading size="md" color="red.700">
              Something went wrong
            </Heading>
            <Text fontSize="sm" color="gray.700">
              An error occurred while rendering this component. This has been logged for review.
            </Text>
            {this.state.error && (
              <Box
                p={3}
                bg="white"
                borderRadius="sm"
                fontSize="xs"
                fontFamily="monospace"
                color="red.600"
              >
                {this.state.error.message}
              </Box>
            )}
            <Button size="sm" colorScheme="red" onClick={this.handleReset} alignSelf="flex-start">
              Try Again
            </Button>
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}
